import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';

import {
  Box, Card, Avatar, Grid, Stack, Typography,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Badge, Paper, CircularProgress
} from '@mui/material';
import {
  Add, ContentCopy, Print, Download, Close, TableRestaurant,
  QrCode2, Phone, LocationOn
} from '@mui/icons-material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h4: { fontWeight: 800, color: '#1a1a1a' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 28 },
});

const LuxuryCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 28,
  padding: '28px 22px',
  textAlign: 'center',
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 5,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '28px 28px 0 0',
  },
}));

const LogoBox = styled(Box)(({ theme }) => ({
  width: 70,
  height: 70,
  borderRadius: 20,
  overflow: 'hidden',
  margin: '0 auto 12px',
  border: '3px solid rgba(212,175,55,0.25)',
  boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
}));

const QRBox = styled(Box)(({ theme }) => ({
  margin: '20px auto',
  width: 180,
  height: 180,
  background: '#ffffff',
  padding: 12,
  borderRadius: 20,
  border: '1px solid #e5e7eb',
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: '100%', height: '100%' },
}));

const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

const Footer = styled(Box)(({ theme }) => ({
  marginTop: 16,
  fontSize: 13,
  color: '#555',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  flexWrap: 'wrap',
  '& i': { color: '#d4af37', fontSize: 14 },
  '& span': { display: 'flex', alignItems: 'center', gap: 5 },
}));

export default function TablesManager() {
  const [tables, setTables] = useState([]);
  const [profile, setProfile] = useState({ name: '', address: '', phone: '', logo_url: '' });
  const [showAddTable, setShowAddTable] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const restaurantId = localStorage.getItem('restaurant_id');
  const api = process.env.REACT_APP_API_URL;
  const base = process.env.REACT_APP_BASE_URL || window.location.origin;

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    Promise.all([loadTables(), loadProfile()]);
  }, [token, navigate]);

  const loadTables = async () => {
    try {
      const res = await axios.get(`${api}/admin-tables.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTables(res.data);
      setFetchError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        setFetchError('Failed to fetch tables. Check API server and .env setup.');
        console.error('Tables fetch error:', err);
      }
    }
  };

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${api}/admin-profile.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const logoUrl = res.data.logo_url ? `${api}/${res.data.logo_url}` : '';

      setProfile({
        name: res.data.name,
        address: res.data.address,
        phone: res.data.phone,
        logo_url: logoUrl,
      });

      if (logoUrl) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setLogoLoaded(true);
        img.onerror = () => setLogoLoaded(true);
        img.src = logoUrl;
      } else {
        setLogoLoaded(true);
      }
      setFetchError('');
    } catch (err) {
      setLogoLoaded(true);
      setFetchError('Failed to fetch profile. Check API server and .env setup.');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTable = async () => {
    if (!newTableNumber.trim()) return alert('Enter table number');
    try {
      const res = await axios.post(
        `${api}/admin-create-table.php`,
        { number: newTableNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTables((prev) => [...prev, res.data]);
      setNewTableNumber('');
      setShowAddTable(false);
      setFetchError('');
    } catch {
      alert('Failed to create table');
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    alert('Link copied!');
  };
const downloadQR = async (table) => {
  const qrSvg = document.querySelector(`[data-table-id="${table.id}"] .qr-svg`);
  if (!qrSvg) return;

  setDownloadingId(table.id);

  try {
    // 1. Clone the SVG and add a white background
    const clone = qrSvg.cloneNode(true);
    const svgNS = 'http://www.w3.org/2000/svg';
    const rect = document.createElementNS(svgNS, 'rect');
    rect.setAttribute('width', '180');
    rect.setAttribute('height', '180');
    rect.setAttribute('fill', '#ffffff');
    clone.insertBefore(rect, clone.firstChild);

    // 2. Serialize SVG → data URL
    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    // 3. Load SVG into an Image
    const img = new Image();
    img.src = svgUrl;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    // 4. Draw on canvas (2× scale = crisp PNG)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 180 * 2;
    canvas.height = 180 * 2;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // 5. Trigger download
    const link = document.createElement('a');
    link.download = `QR-Table-${table.number}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    URL.revokeObjectURL(svgUrl);
  } catch (err) {
    console.error(err);
    alert('QR download failed');
  } finally {
    setDownloadingId(null);
  }
};
 const handlePrint = (table) => {
  const cardContainer = document.querySelector(`[data-table-id="${table.id}"]`);
  if (!cardContainer) return;

  if (profile.logo_url && !logoLoaded) {
    alert('Logo is still loading. Please wait.');
    return;
  }

  const controlBtns = cardContainer.querySelectorAll('.capture-control-btn');
  controlBtns.forEach(btn => btn.style.display = 'none');

  const printWin = window.open('', '_blank');
  if (!printWin) {
    alert('Pop-up blocked! Allow pop-ups.');
    controlBtns.forEach(btn => btn.style.display = '');
    return;
  }

  const cardClone = cardContainer.querySelector('.MuiCard-root').cloneNode(true);
  const logoImg = cardClone.querySelector('img[alt="Logo"]');
  if (logoImg) {
    logoImg.src = profile.logo_url || 'https://via.placeholder.com/70?text=LOGO';
  }

  // FORCE KILL CURSOR & SELECTION
  cardClone.querySelectorAll('*').forEach(el => {
    el.style.userSelect = 'none';
    el.style.caretColor = 'transparent';
    el.style.pointerEvents = 'none';
    if (el.isContentEditable) el.contentEditable = false;
  });

  const allStyles = Array.from(document.styleSheets)
    .map(s => { try { return Array.from(s.cssRules).map(r => r.cssText).join(''); } catch { return ''; } })
    .join('');

  const CLEAN_PRINT_CSS = `
    .MuiCard-root::before,
    .MuiCard-root::after { display:none !important; }

    *, *::before, *::after {
      border:none !important;
      outline:none !important;
      box-shadow:none !important;
      caret-color:transparent !important;
      user-select:none !important;
    }

    *::selection, *::-moz-selection { background:transparent !important; }

    .MuiCard-root { 
      background:#fff !important; 
      padding:28px 22px !important; 
      overflow:visible !important; 
    }

    .card-wrapper { 
      transform:scale(1.85); 
      transform-origin:center; 
    }
  `;

  printWin.document.write(`
    <!DOCTYPE html>
    <html><head>
      <meta charset="UTF-8">
      <title>Table ${table.number}</title>
      <style>
        @page { size:A4 portrait; margin:0; }
        *,*::before,*::after{box-sizing:border-box;}
        html,body{
          margin:0;padding:0;width:210mm;height:297mm;
          background:#f8f8f8;display:flex;align-items:center;justify-content:center;
          font-family:'Inter','Poppins',sans-serif;
        }
        .a4{width:210mm;height:297mm;display:flex;align-items:center;justify-content:center;padding:20mm;}
        .card-wrapper{background:#fff;border-radius:32px;padding:16px;}
        img,svg{max-width:100%;height:auto;display:block;}
      </style>
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" rel="stylesheet">
      <style>${allStyles}</style>
      <style>${CLEAN_PRINT_CSS}</style>
    </head>
    <body>
      <div class="a4"><div class="card-wrapper">${cardClone.outerHTML}</div></div>
      <script>window.onload=()=>{setTimeout(()=>{window.print();window.close();},600);}</script>
    </body></html>
  `);

  printWin.document.close();

  setTimeout(() => controlBtns.forEach(b => b.style.display = ''), 2000);
};

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <CircularProgress color="primary" />
        <Typography variant="h6" mt={2} color="text.primary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#ffffff', minHeight: '100vh' }}>
        {fetchError && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffe8e0', color: '#c00', border: '1px solid #c00', textAlign: 'center' }}>
            {fetchError}
          </Paper>
        )}

        {/* HEADER */}
        <Stack direction="row" justifyContent="space-between" mb={4} alignItems="center">
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#d4af37', width: 56, height: 56 }}>
              <TableRestaurant />
            </Avatar>
            <Box>
              <Typography variant="h4" color="text.primary">
                Table QR Codes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Print-ready luxury design
              </Typography>
            </Box>
            <Badge badgeContent={tables.length} color="primary" />
          </Stack>

          <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddTable(true)}>
            Add Table
          </Button>
        </Stack>

        {/* ADD TABLE DIALOG */}
        <Dialog open={showAddTable} onClose={() => setShowAddTable(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff' }}>
            <QrCode2 sx={{ mr: 1 }} /> Create Table
            <IconButton sx={{ position: 'absolute', right: 12, top: 12, color: '#fff' }} onClick={() => setShowAddTable(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 4 }}>
            <TextField fullWidth label="Table Number" value={newTableNumber} onChange={(e) => setNewTableNumber(e.target.value)} autoFocus />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowAddTable(false)}>Cancel</Button>
            <Button variant="contained" onClick={createTable}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* GRID */}
        {tables.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: '#f9f9f9' }}>
            <QrCode2 sx={{ fontSize: 64, color: '#d4af37', mb: 2 }} />
            <Typography variant="h5" color="text.primary">
              No Tables
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddTable(true)} sx={{ mt: 2 }}>
              Add First Table
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={4}>
            {tables.map((table) => {
              const url = `${base}/r/${restaurantId}/t/${table.id}?s=${table.secret_token}`;
              const isDownloading = downloadingId === table.id;

              return (
                <Grid item xs={12} sm={6} lg={4} key={table.id}>
                  <div
                    data-table-id={table.id}
                    style={{
                      padding: '12px',
                      background: '#ffffff',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <LuxuryCard elevation={0}>
                      <LogoBox>
                        <img
                          src={profile.logo_url || 'https://via.placeholder.com/70?text=LOGO'}
                          alt="Logo"
                          style={{ display: logoLoaded ? 'block' : 'none' }}
                        />
                        {!logoLoaded && <Box sx={{ width: '100%', height: '100%', bgcolor: '#f0f0f0' }} />}
                      </LogoBox>

                      <GoldText variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, textTransform : 'uppercase' }}>
                        {profile.name}
                      </GoldText>

                      {profile.address && (
                        <Typography
                          variant="body2"
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5, color: '#555', fontSize: '0.85rem' }}
                        >
                          <LocationOn fontSize="small" /> {profile.address}
                        </Typography>
                      )}

                      {profile.phone && (
                        <Typography
                          variant="body2"
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5, color: '#555', fontSize: '0.85rem' }}
                        >
                          <Phone fontSize="small" /> {profile.phone}
                        </Typography>
                      )}

                      <QRBox>
                        <QRCodeSVG
                          value={url}
                          size={180}
                          level="H"
                          className="qr-svg"
                        />
                      </QRBox>

                      {/* TABLE NO: — CURSOR-FREE */}
                     <Typography 
                        variant="h6" 
                        mt={1} 
                        color="text.primary"
                        contentEditable={false}
                        sx={{ 
                          fontSize: { xs: '1rem', sm: '1.1rem' },
                          userSelect: 'none !important',
                          pointerEvents: 'none',
                          caretColor: 'transparent !important'
                        }}
                      >
                        Table No: {table.number}
                      </Typography>

                      <Footer>
                        <span>
                          <i className="fas fa-qrcode"></i> Scan to Order
                        </span>
                        <span>
                          <i className="fas fa-utensils"></i> View Menu
                        </span>
                        <span>
                          <i className="fas fa-bell"></i> Place Order
                        </span>
                      </Footer>

                      <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                        <Button size="small" startIcon={<ContentCopy />} onClick={() => copyLink(url)} variant="outlined" className="capture-control-btn">
                          Copy
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Print />}
                          onClick={() => handlePrint(table)}
                          color="primary"
                          variant="contained"
                          disabled={profile.logo_url && !logoLoaded}
                          className="capture-control-btn"
                        >
                          Print
                        </Button>
                        <Button
                          size="small"
                          startIcon={isDownloading ? <CircularProgress size={16} /> : <Download />}
                          onClick={() => downloadQR(table)}
                          color="secondary"
                          variant="contained"
                          disabled={isDownloading || (profile.logo_url && !logoLoaded)}
                          className="capture-control-btn"
                        >
                          {isDownloading ? '...' : 'PNG'}
                        </Button>
                      </Stack>
                    </LuxuryCard>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
    </ThemeProvider>
  );
}